import { useState } from 'react'
import { useBahasa } from '../../../app/i18n'
import {
  useFieldProfil, useSetelanMeeting, useSimpanSetelanMeeting,
} from '../../../domains/tenancy/api'
import {
  AreaTeks, Bendera, Body, Gagal, Isian, KV, KVBaris, Kolom2, Memuat, Panel, Seg, Sub,
  Tombol, useSimpanKepala,
} from '../../../shared/ui'

/**
 * SYARAT & FORMAT MEETING — kapan bot boleh menawarkan, dan pertemuan bentuk apa.
 *
 * Sampai migrasi 0030 keempatnya dipatok di kode. Klien yang cuma melayani online tidak
 * punya cara menyatakannya, dan bot akan menawarkan tatap muka atas namanya.
 *
 * "Data harus lengkap" MEMAKAI FIELD PROFIL YANG SUDAH ADA, bukan daftar tulis tangan.
 * Kalau daftarnya ditulis terpisah, ia basi begitu admin menambah atau menonaktifkan satu
 * field — dan syarat yang menunjuk field yang sudah tidak ada berarti bot tidak akan
 * pernah menganggap datanya lengkap, tanpa satu pun galat.
 */
export default function SyaratMeeting() {
  const { t } = useBahasa()
  const q = useSetelanMeeting()
  const field = useFieldProfil()
  const simpan = useSimpanSetelanMeeting()
  // Suntingan ditumpuk di atas data — lihat catatan yang sama di `Persona.jsx`.
  const [ubah, setUbah] = useState({})

  const f = {
    ...(q.data ?? {}), ...ubah,
    wajib_lengkap: ubah.wajib_lengkap ?? [...(q.data?.wajib_lengkap ?? [])],
  }
  const set = (k, v) => setUbah((x) => ({ ...x, [k]: v }))
  const butuhLokasi = f.bentuk !== 'online'
  const bisa = Boolean(!butuhLokasi || (f.lokasi_offline || '').trim())

  const daftarField = (field.data ?? []).filter((x) => x.aktif ?? x.is_active ?? true)

  // DI ATAS SEMUA `return` AWAL — lihat catatan yang sama di `Persona.jsx`.
  useSimpanKepala(
    () => simpan.mutate(
      { ...f, lokasi_offline: (f.lokasi_offline || '').trim() || null },
      { onSuccess: () => setUbah({}) },
    ),
    { bisa, menyimpan: simpan.isPending },
  )

  if (q.isLoading) return <Memuat tinggi={6} />
  if (q.isError) return <Gagal galat={q.error} ulangi={q.refetch} />

  return (
    <div className="flex flex-col gap-3.5">
      <Kolom2>
      <Panel judul={t('ta.syaratJudul')}>
        <Body>
          <KV>
            <KVBaris label={t('ta.dataLengkap')}>
              {field.isLoading && <Memuat tinggi={2} />}
              {daftarField.length === 0 && !field.isLoading && (
                <span className="text-[12px] text-warn">{t('ta.tidakAdaField')}</span>
              )}
              <div className="flex flex-wrap gap-1.5">
                {daftarField.map((x) => {
                  const aktif = f.wajib_lengkap.includes(x.kunci)
                  return (
                    <Tombol
                      key={x.kunci}
                      kecil
                      aria-pressed={aktif}
                      jenis={aktif ? 'utama' : 'sekunder'}
                      onClick={() => set(
                        'wajib_lengkap',
                        aktif ? f.wajib_lengkap.filter((y) => y !== x.kunci)
                              : [...f.wajib_lengkap, x.kunci],
                      )}
                    >
                      {x.label || x.kunci}
                    </Tombol>
                  )
                })}
              </div>
              <Sub>{t('ta.dataLengkapCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.minatMinimal')}>
              <Seg
                nilai={String(f.minat_minimal)}
                set={(v) => set('minat_minimal', Number(v))}
                daftar={[1, 2, 3, 4, 5].map((n) => ({ id: String(n), label: `${n}` }))}
              />
              <Sub>{t('ta.minatCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.jedaHari')}>
              <Isian
                type="number"
                min={0}
                max={30}
                className="max-w-[110px]"
                value={f.jeda_hari}
                onChange={(e) => set('jeda_hari', Number(e.target.value))}
              />
              <Sub>{t('ta.jedaCatatan')}</Sub>
            </KVBaris>

            {/* JEDA ANTAR MEETING — beda pertanyaan dari "Jarak paling cepat" di atas.
                Yang itu menjawab "paling cepat berapa hari dari sekarang"; yang ini
                "berapa napas di antara dua pertemuan pada hari yang sama". Tanpa ini bot
                boleh menawarkan dua meeting yang menempel persis. */}
            <KVBaris label={t('ta.jedaMenit')}>
              <Seg
                nilai={String(f.jeda_menit ?? 30)}
                set={(v) => set('jeda_menit', Number(v))}
                daftar={[0, 15, 30, 60].map((n) => ({ id: String(n), label: `${n} mnt` }))}
              />
              <Sub>{t('ta.jedaMenitCatatan')}</Sub>
            </KVBaris>

            <KVBaris label={t('ta.durasi')}>
              <Seg
                nilai={String(f.durasi_menit)}
                set={(v) => set('durasi_menit', Number(v))}
                daftar={[30, 45, 60, 90].map((n) => ({ id: String(n), label: `${n} mnt` }))}
              />
            </KVBaris>
          </KV>
        </Body>
      </Panel>

      <Panel judul={t('ta.bentukJudul')}>
        <Body>
          <KV>
            <KVBaris label={t('ta.format')}>
              <Seg
                nilai={f.bentuk}
                set={(v) => set('bentuk', v)}
                daftar={[
                  { id: 'online', label: t('ta.onlineSaja') },
                  { id: 'offline', label: t('ta.offlineSaja') },
                  { id: 'keduanya', label: t('ta.keduanya') },
                ]}
              />
            </KVBaris>

            {/* Muncul hanya kalau relevan, dan WAJIB saat muncul. Bentuk yang memuat tatap
                muka tanpa lokasi adalah janji yang tidak bisa ditepati — bot menawarkan
                pertemuan lalu tidak punya tempat untuk disebut. Ditolak juga oleh CHECK
                di database, tapi di sini pesannya menyebut apa yang harus diisi. */}
            {butuhLokasi && (
              <KVBaris label={t('ta.lokasiOffline')}>
                <AreaTeks
                  baris={2}
                  placeholder="Jl. Contoh No. 1, Jakarta Selatan"
                  value={f.lokasi_offline ?? ''}
                  onChange={(e) => set('lokasi_offline', e.target.value)}
                />
                <Sub>{t('ta.lokasiCatatan')}</Sub>
              </KVBaris>
            )}
          </KV>
        </Body>

      </Panel>
      </Kolom2>

      {/* GALAT tetap di halaman, tombolnya TIDAK: ia pindah ke kepala halaman sesuai
          rancangan. Pesan galat yang ikut pindah ke kepala akan berdesakan dengan judul
          dan keterangan, dan yang paling sering muncul di sini kalimat panjang dari
          backend. */}
      {simpan.isError && (
        <Bendera nada="bad" role="alert">{simpan.error?.message}</Bendera>
      )}
      {butuhLokasi && !bisa && <Sub className="text-warn">{t('ta.lokasiWajib')}</Sub>}
    </div>
  )
}
